<script lang="ts">
    import { Component, Prop, Vue } from 'vue-facing-decorator';
    import { useBestSeatStore } from "@/domain/ticket-selection/application/BestSeat.store";
    import { PriceLevelStateCollectionProvider, usePromotionStore } from "@/domain/ticket-selection";
    import ButtonComponent from "@/components/atoms/button/Button.vue";
    import StepperComponent from "@/components/atoms/stepper/Stepper.vue";
    import PriceLevelComponent from "@/components/ticket-selection/best-seat-selection/PriceLevel.vue";
    import { PriceLevel } from "webshop-api";
    import { CurrencyHandler, IconName, PerformanceQASelectors, TranslationKey } from 'webshop-modules';
    import { StepperState } from "@/components/atoms/stepper/stepper-state.interface";
    import {
        HoldState,
        PriceLevelState,
        PromotionHoldState,
        TicketTypeState
    } from "@/domain/ticket-selection/entities";
    import { ArrayHelper } from "webshop-modules/helper/Array.helper";
    import { TranslationPlugin } from '@/plugins/translation.plugin';
    import TicketFeeInfo from "@/components/atoms/ticket-fee/TicketFeeInfo.vue";

    @Component({
        name: 'SingleTicketTypeSelectionComponent',
        components: {
            TicketFeeInfo,
            ButtonComponent,
            PriceLevelComponent,
            StepperComponent
        }
    })
    export default class SingleTicketTypeSelectionComponent extends Vue {

        @Prop() public data!: Array<PriceLevel>;
        @Prop() public eventName!: string;

        public readonly IconName: typeof IconName = IconName;
        public readonly Translation: typeof TranslationKey = TranslationKey;
        public readonly PerformanceQASelectors: typeof PerformanceQASelectors = PerformanceQASelectors;
        public readonly bestSeatStore = useBestSeatStore();
        public readonly promotionStore = usePromotionStore();

        public readonly CurrencyHandler = new CurrencyHandler(
            this.bestSeatStore.currency ?? '',
            this.bestSeatStore.currencyOptions?.currency ?? '',
            this.bestSeatStore.currencyOptions?.conversionRate ?? '1',
            this.bestSeatStore.currencyOptions?.showDoubleCurrency ?? false
        );

        public submitLabel = '';

        public get stepperState(): StepperState {
            const step = this.getStepConfig();
            const amount = PriceLevelStateCollectionProvider.getHighestTicketTypeAmount(this.bestSeatStore.priceLevels);

            return {
                step,
                amount,
                canDecrease: this.canStepperDecrease(amount, step),
                canIncrease: this.canStepperIncrease(amount, step),
            };
        }

        public get activePromotionId(): number {
            return this.promotionStore.activePromotion ? this.promotionStore.activePromotion.id! : 0;
        }

        public get activePriceLevel(): PriceLevelState | undefined {
            return this.bestSeatStore.priceLevels.find(priceLevel => priceLevel.active);
        }

        public priceFor(priceLevel: PriceLevel) {
            if (!priceLevel.promotionHolds) { return 0; }

            const promotionHold = priceLevel.promotionHolds.find(p => p.id === this.activePromotionId);
            if (!promotionHold || !promotionHold.holds) { return 0; }

            const hold = promotionHold.holds
                .filter(hold => hold.number === (this.bestSeatStore.activeHold ? this.bestSeatStore.activeHold.number : 0))
                .reduce(ArrayHelper.LastValueReducer);

            if (!hold || !hold.ticketTypes || !hold.ticketTypes.length) { return 0; }
            return hold.ticketTypes.reduce(ArrayHelper.LastValueReducer).price || 0;
        }

        public canDisplayNoticeSectionFor(priceLevelId: number | undefined): boolean {
            const priceLevelState = this.priceLevelState(priceLevelId);

            if (!priceLevelState) {
                return false;
            }

            return priceLevelState.active &&
                (!this.isMinRequirementReachedFor(priceLevelState) || !this.isMaxRequirementReachedBy(priceLevelState));
        }

        public shouldDisplayMaxErrorNoticeFor(priceLevelId: number | undefined): boolean {
            const priceLevelState = this.priceLevelState(priceLevelId);

            if (!priceLevelState) {
                return false;
            }

            return this.isMinRequirementReachedFor(priceLevelState) && !this.isMaxRequirementReachedBy(priceLevelState)
        }

        private getStepConfig(): number {
            return this.bestSeatStore.priceLevels.reduce((priceLevelStep, priceLevel) => {
                const nextStep = priceLevel.promotionHolds
                    .filter(promotion => promotion.active)
                    .reduce((promoStep: number, promotion: PromotionHoldState) => {
                        const nextPromoStep = promotion.holds
                            .filter(hold => hold.active)
                            .reduce((holdStep: number, hold: HoldState) => {
                                const nextHoldStep = hold.ticketTypes.reduce(
                                    (ticketTypeStep, ticketType) => ArrayHelper.MinValueReducer(ticketTypeStep, ticketType.step), 0);
                                return ArrayHelper.MinValueReducer(holdStep, nextHoldStep);
                            }, 0);
                        return ArrayHelper.MinValueReducer(promoStep, nextPromoStep);
                    }, 0);
                return ArrayHelper.MinValueReducer(priceLevelStep, nextStep);
            }, 0);
        }

        private canStepperDecrease(amount: number, step: number): boolean {
            return amount - step >= 0 && amount > 0;
        }

        private canStepperIncrease(amount: number, step: number): boolean {
            let ticketMax: number = PriceLevelStateCollectionProvider.getHighestHoldMaximum(
                this.bestSeatStore.priceLevels
            );

            return amount + step <= ticketMax && ticketMax > 0;
        }

        private isPriceLevelActive(priceLevel: PriceLevel): boolean {
            if (!this.activePriceLevel) {
                return false;
            }

            return this.activePriceLevel.priceLevelId === priceLevel.priceLevelId;
        }

        public get ticketsPrice(): number {
            if (!this.activePriceLevel) {
                return 0;
            }

            const priceLevel = this.data.find(priceLevel =>
                priceLevel.priceLevelId === this.activePriceLevel!.priceLevelId);

            if (!priceLevel) {
                return 0;
            }

            return (this.priceFor(priceLevel) * 100) * this.stepperState.amount / 100;
        }

        public getSelectedTicketsPriceList(): number[] {
            return this.data.reduce((ticketPriceList: number[], pricelevel: PriceLevel) => {
                if (this.isPriceLevelActive(pricelevel)) {
                    for (let i = 0; i < this.stepperState.amount; i++) {
                        ticketPriceList.push(this.priceFor(pricelevel));
                    }
                }

                return ticketPriceList;
            }, []);
        }

        public get submitButtonLabel(): string  {
            return TranslationPlugin.decorateWithParams(this.submitLabel, {
                    key: this.Translation.Ticket_selection_ticket_count_submit,
                    keyValues: JSON.stringify({
                        'amount': this.stepperState.amount,
                        'price': this.CurrencyHandler.displayPrimaryCurrency(this.ticketsPrice)
                    }),
                    identifierStart: '{',
                    identifierEnd: '}'
                });
        }

        public isPriceLevelAvailable(priceLevel: PriceLevel, state: PriceLevelState | undefined): boolean {
            if (!state) {
                return false;
            }

            if (!priceLevel.promotionHolds) {
                return false;
            }

            const activePromotionHold = priceLevel.promotionHolds.find(p => p.id === this.activePromotionId);
            if (!activePromotionHold || !activePromotionHold.holds) {
                return false;
            }

            const activeHold = activePromotionHold.holds!.find(h => h.number === 0);
            if (!activeHold) {
                return false;
            }

            return activeHold.totalFreeSeats !== undefined && activeHold.totalFreeSeats > 0
                && activeHold.maxFreeConnectedSeats !== undefined && activeHold.maxFreeConnectedSeats > 0;
        }

        public getTicketTypeFor(state: PriceLevelState): TicketTypeState | null {
            if (!state.promotionHolds) {
                return null;
            }

            const activePromotion = state.promotionHolds.find(p => p.active);
            if (!activePromotion || !activePromotion.holds) {
                return null;
            }

            const activeHold = activePromotion.holds!.find(h => h.active);
            if (!activeHold || !activeHold.ticketTypes || !(activeHold.ticketTypes.length > 0)) {
                return null;
            }

            return activeHold.ticketTypes[0];
        }

        public getMaxRestrictionFor(state: PriceLevelState | undefined): number {
            if (!state) {
                return PriceLevelStateCollectionProvider.getHighestHoldMaximum(this.bestSeatStore.priceLevels);
            }

            const tt = this.getTicketTypeFor(state);

            return tt !== null && tt.max
                ? tt.max
                : PriceLevelStateCollectionProvider.getHighestHoldMaximum(this.bestSeatStore.priceLevels);
        }

        public getMinRestrictionFor(state: PriceLevelState | undefined): number {
            if (!state) {
                return 0;
            }

            const tt = this.getTicketTypeFor(state);
            return tt !== null && tt.min ? tt.min : 0;
        }

        public isMaxRequirementReachedBy(state: PriceLevelState | undefined): boolean {
            if (!state) {
                return false;
            }

            return this.getMaxRestrictionFor(state) >= this.stepperState.amount;
        }

        public isMinRequirementReachedFor(pricelevel: PriceLevelState | undefined): boolean {
            if (!pricelevel) {
                return false;
            }

            return this.getMinRestrictionFor(pricelevel) <= this.stepperState.amount;
        }

        public priceLevelState(priceLevelId: number | undefined): PriceLevelState | undefined {
            return this.bestSeatStore.priceLevels.find(
                (priceLevel: PriceLevelState) => priceLevel.priceLevelId === priceLevelId
            );
        }

        public handlePriceLevelSelection(priceLevelState: PriceLevelState | undefined, priceLevel: PriceLevel): void {
            if (!priceLevelState) {
                return undefined;
            }

            if (!this.isPriceLevelAvailable(priceLevel, priceLevelState)) {
                return undefined;
            }

            const { activatePriceLevel } = this.bestSeatStore;
            activatePriceLevel(priceLevelState);

        }

        public handleStepperStateChange(state: StepperState): void {
          const { handleSingleSelectionStepperChange } = this.bestSeatStore;
          handleSingleSelectionStepperChange(state);
        }

        public handleSubmitSelection(state: PriceLevelState|undefined): void {
            if (!state || !state.active || !state.canSubmit || this.stepperState.amount === 0) {
                return undefined;
            }

            const { submitSelection } = this.bestSeatStore;
            submitSelection({
              state: state,
              promotion: this.promotionStore.activePromotion,
              promotionCode: this.promotionStore.promotionCode,
              eventName: this.eventName,
            });
        }

        /** Created lifecycle handler method */
        protected async created() {
            this.submitLabel = await this.$translationService
                .getTranslationFor(this.Translation.Ticket_selection_ticket_count_submit);

            const stepperState = this.stepperState;
            const step = this.getStepConfig();
            const commonTicketTypeMaximum = PriceLevelStateCollectionProvider.getLowestTicketTypeMaximum(
                this.bestSeatStore.priceLevels,
                this.bestSeatStore.maxTickets
            );

            stepperState.amount = 0;

            if (step === 1 && commonTicketTypeMaximum <= 2) {
                stepperState.amount = commonTicketTypeMaximum;
            }

            if (step === 1 && commonTicketTypeMaximum > 2) {
                stepperState.amount = 2;
            }

            if (step > 1 && step <= commonTicketTypeMaximum) {
                stepperState.amount = step;
            }

            stepperState.canDecrease = this.canStepperDecrease(stepperState.amount, stepperState.step);
            stepperState.canIncrease = this.canStepperIncrease(stepperState.amount, stepperState.step);

            const { handleSingleSelectionStepperChange } = this.bestSeatStore;
            handleSingleSelectionStepperChange(stepperState);
        }

    }
</script>

<template>
    <div id="single-ticket-type-selection"
         class="o-singleTicketTypeSelection">
        <div class="o-singleTicketTypeSelection__heading">
            <p v-translate="{ key: Translation.Ticket_selection_single_type_selection_heading }">
                Set desired amount of tickets, then choose the category</p>

            <stepper-component
                :state="stepperState"
                v-on:state-change="handleStepperStateChange">
            </stepper-component>
        </div>

        <div class="o-singleTicketTypeSelection__options">
            <template v-for="priceLevel in data" :key="priceLevel.priceLevelId">
                <div :class="{ '-available' : isPriceLevelAvailable(priceLevel, priceLevelState(priceLevel.priceLevelId))}"
                     class="m-priceLevel -alternative"
                     v-on:click="handlePriceLevelSelection(priceLevelState(priceLevel.priceLevelId), priceLevel)"
                     :data-qa="PerformanceQASelectors.PRICE_LEVEL_ALTERNATIVE">
                    <div class="m-priceLevel__section -information -alternative">
                        <h4 class="m-priceLevel__title -alternative">{{ priceLevelState(priceLevel.priceLevelId).text }}</h4>
                        <p class="m-priceLevel__description -alternative">{{ priceLevelState(priceLevel.priceLevelId).description }}</p>
                    </div>

                    <div class="m-priceLevel__section -alternative">
                        <div class="m-priceLevel__price">
                            <p class="m-priceLevel__pricePrimary">
                                {{
                                    CurrencyHandler.displayPrimaryCurrency(priceFor(priceLevel))
                                }}
                            </p>
                            <small v-if="CurrencyHandler.isSecondaryCurrencyDisplayed" class="m-priceLevel__priceSecondary">
                                {{
                                    CurrencyHandler.displaySecondaryCurrency(priceFor(priceLevel))
                                }}
                            </small>
                        </div>

                        <p v-if="!isPriceLevelAvailable(priceLevel, priceLevelState(priceLevel.priceLevelId))" class="m-priceLevel__unavailable">
                            {{ priceLevel.statusDescription }}</p>

                        <div v-if="isPriceLevelAvailable(priceLevel, priceLevelState(priceLevel.priceLevelId))"
                             :class="{ '-is-active': priceLevelState(priceLevel.priceLevelId)!.active }"
                             class="a-ticketModeOption__indicator">
                            <div class="a-ticketModeOption__indicatorCircle"
                                 :data-qa="PerformanceQASelectors.PRICE_LEVEL_CIRCLE_INDICATOR">
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="canDisplayNoticeSectionFor(priceLevel.priceLevelId)" class="m-priceLevel__section -notice">
                    <p v-if="!isMinRequirementReachedFor(priceLevelState(priceLevel.priceLevelId))"
                       v-translate="{
                               key: Translation.Ticket_selection_min_ticket_type,
                               keyValues: JSON.stringify({ 'placeholder-min-value': getMinRestrictionFor(priceLevelState(priceLevel.priceLevelId)) }),
                               identifierStart: '{',
                               identifierEnd: '}'
                       }"
                       class="a-notice -error">Minimum amount restriction not reached</p>

                    <p v-if="shouldDisplayMaxErrorNoticeFor(priceLevel.priceLevelId)"
                       v-translate="{
                               key: Translation.Seatmap_max_selection_error,
                               keyValues: JSON.stringify({ 'placeholder-max-selection': getMaxRestrictionFor(priceLevelState(priceLevel.priceLevelId)) }),
                               identifierStart: '{',
                               identifierEnd: '}',
                       }"
                       class="a-notice -error">Max amount restriction exceeded</p>
                </div>
            </template>
        </div>

        <div :class="CurrencyHandler.isSecondaryCurrencyDisplayed ? '-has-secondary-currency' : ''"
              class="o-singleTicketTypeSelection__action">
            <div class="o-singleTicketTypeSelection__actionWrapper">
                <button-component
                    :active="activePriceLevel && activePriceLevel.canSubmit"
                    :icon-name="IconName.Cart"
                    :label="submitButtonLabel"
                    :secondary-label="CurrencyHandler.displaySecondaryCurrency(ticketsPrice)"
                    :qa-selector="PerformanceQASelectors.PRICE_LEVEL_SUBMIT"
                    v-on:submit="handleSubmitSelection(activePriceLevel)"
                />
                <TicketFeeInfo :fee-list="bestSeatStore.fees"
                               :ticket-price-list="getSelectedTicketsPriceList()"
                               :currency-handler="CurrencyHandler"
                               :has-opacity="stepperState.amount === 0">
                </TicketFeeInfo>
            </div>
        </div>
    </div>
</template>
